import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import { Link } from 'react-router-dom'
import { Container, Card, Reveal } from '@/components/ui'
import { useCmsProducts } from '@/hooks/useCmsProducts'
import { useCmsMediaIndex } from '@/hooks/useCmsMediaIndex'
import { localizeProducts, type LocalizedProduct } from '@/content/cms/productLocale'
import CmsImage from '@/components/media/CmsImage'
import { cmsMediaLargestWebpUrl } from '@/utils/cmsMediaUrl'
import {
  productExternalCta,
  productRoute,
} from '@/content/productRoutes'
import { buildLocalePath, type Locale } from '@/utils/localePath'

/**
 * Curating the homepage grid (precedence rule):
 *   1. If settings.slugs (cms_home_sections.settings_json.slugs) lists slugs,
 *      use that subset verbatim. Operator-overridden.
 *   2. Else if any products have is_featured=1, show those.
 *   3. Else show everything (preserves "ship new products straight to the
 *      homepage" without a config edit).
 *
 * The explicit fallback chain is what the plan file documents. The middle
 * branch protects the "show 3 featured on the homepage" UX while letting
 * an operator add a new feature flag without breaking the homepage.
 */
type FeatureItem = string | { title: string; text: string }

/**
 * The `sizes` for a product card image.
 *
 * The grid is responsive: 1 col on mobile, 2 cols on tablet (md),
 * 4 cols on desktop (lg). Inside the 1200px container this gives
 * ≈50vw on tablet and ≈25vw on desktop — both overstate slightly
 * (safe direction for `sizes`).
 */
const PRODUCT_CARD_IMAGE_SIZES = '(min-width: 768px) 50vw, (min-width: 1024px) 25vw, 100vw'

export default function ProductsGrid({ settings }: { settings?: { slugs?: string[] } } = {}) {
  const { t, i18n } = useTranslation()
  // Every public URL carries a locale prefix; a bare `/products/…` link
  // would be bounced by the bare-path redirect map into the DEFAULT
  // locale and drop a visitor who is reading English.
  const locale = (i18n.language || 'zh-Hant') as Locale
  const mediaIndex = useCmsMediaIndex()

  // The generated index carries all three locales' copy — resolve it to
  // the active locale before rendering.
  const products = localizeProducts(useCmsProducts(), locale)

  const wanted = settings?.slugs ?? []
  let visible: LocalizedProduct[]
  if (wanted.length > 0) {
    // branch 1 — operator subset
    visible = products.filter((p) => wanted.includes(p.slug))
  } else if (products.some((p) => p.is_featured === 1 && p.published_at !== null)) {
    // branch 2 — is_featured fallback
    visible = products.filter((p) => p.is_featured === 1 && p.published_at !== null)
  } else {
    // branch 3 — published-everything fallback (never empty if the seed ran)
    visible = products.filter((p) => p.published_at !== null)
  }
  if (visible.length === 0) return null

  return (
    <section className="py-section-y px-section-x bg-surface" aria-labelledby="products-grid-title">
      <Container>
        <div className="text-center mb-6 md:mb-8">
          <span className="section-title__tagline inline-block mb-3 !hidden">
            {t('home.products.tag')}
          </span>
          <h2
            id="products-grid-title"
            className="section-title__title text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-ink"
          >
            {t('home.products.title')}
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-gap-cards md:gap-gap-cards">
          {visible.map((product, idx) => {
            const features = (product.features ?? []) as FeatureItem[]
            // The slug is the CMS's internal id; the route it serves may
            // differ (ai-advisor → /products/smart-cindy).
            const detailTo = buildLocalePath(productRoute(product.slug), locale)
            // A product whose call to action lives on ANOTHER website
            // (CSP → csp.yada.hk). Null for every product that stays on
            // this site, whose button keeps linking to its own page.
            const externalCta = productExternalCta(product.slug)
            // "Has an image" means "has a WebP rung" — the shared <CmsImage>
            // renders nothing for a row without one (a PDF cover would have
            // been a broken image), so the icon tile takes over.
            const hasImage = Boolean(
              product.image_uuid && cmsMediaLargestWebpUrl(product.image_uuid, mediaIndex),
            )
            return (
              <Reveal key={product.slug} delay={idx * 0.1}>
                <Card hover bordered className="h-full flex flex-col">
                  {/* Icon with gradient bg */}
                  {hasImage ? (
                    <div className="aspect-[4/3] mb-4 rounded-yada-lg overflow-hidden bg-surface-2">
                      <CmsImage
                        uuid={product.image_uuid}
                        // An EMPTY product alt falls back to the media
                        // library's alt for the reader's script.
                        alt={product.image_alt || undefined}
                        sizes={PRODUCT_CARD_IMAGE_SIZES}
                        // `object-contain`, not `object-cover`: the product
                        // artwork is a poster/logo with its own composition,
                        // and cropping it to the 4:3 box cut the subject off.
                        // The box keeps its ratio and the letterbox shows the
                        // `bg-surface-2` behind it.
                        className="w-full h-full object-contain"
                      />
                    </div>
                  ) : (
                    <div
                      className={`w-16 h-16 rounded-yada-xl bg-gradient-to-br ${product.accent_gradient} flex items-center justify-center mb-6 shadow-yada`}
                    >
                      <Icon
                        icon={product.icon}
                        className="text-surface"
                        style={{ fontSize: '2rem' }}
                      />
                    </div>
                  )}

                  {product.badge && (
                    <span className="inline-block self-start bg-accent/20 text-ink-soft px-3 py-1 rounded-yada-full text-xs font-semibold mb-3">
                      {product.badge}
                    </span>
                  )}

                  {/* The title is plain text for every product whose
                      button leads to its own page. For a product whose
                      button leaves the site (CSP) the title becomes the
                      link to that product's page HERE, so the product
                      still has an on-site entry point. The <h3> stays
                      where it is and the anchor sits inside it, so the
                      card's structure does not change. */}
                  <h3 className="text-xl font-bold text-ink mb-3">
                    {externalCta ? (
                      <Link
                        to={detailTo}
                        className="text-ink hover:text-ink hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                      >
                        {product.title}
                      </Link>
                    ) : (
                      product.title
                    )}
                  </h3>

                  <p className="text-ink-soft leading-relaxed mb-6 flex-1">
                    {product.description}
                  </p>

                  {features.length > 0 && (
                    <ul className="hidden space-y-2 mb-6 text-sm text-ink-soft">
                      {features.slice(0, 3).map((feat, i) => {
                        const text = typeof feat === 'string' ? feat : feat.title
                        return (
                          <li key={i} className="flex items-start gap-2">
                            <Icon
                              icon="lucide:check"
                              className="text-primary mt-0.5 flex-shrink-0"
                            />
                            <span className="line-clamp-2">{text}</span>
                          </li>
                        )
                      })}
                    </ul>
                  )}

                  {externalCta ? (
                    /* The product's own website — a plain <a>, opened in
                       a new tab. The URL was validated by
                       `productExternalCta` before it reached the href. */
                    <a
                      href={externalCta}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-primary font-semibold text-sm hover:gap-2 transition-all"
                    >
                      <span>{t('common.viewDetails')}</span>
                      <Icon icon="lucide:arrow-right" />
                    </a>
                  ) : (
                    <Link
                      to={detailTo}
                      className="inline-flex items-center gap-1 text-primary font-semibold text-sm hover:gap-2 transition-all"
                    >
                      <span>{t('common.viewDetails')}</span>
                      <Icon icon="lucide:arrow-right" />
                    </Link>
                  )}
                </Card>
              </Reveal>
            )
          })}
        </div>
      </Container>
    </section>
  )
}
